import { ACTIVE_NETWORK } from '../config/networks';

/**
 * Chunked event scanner — works around RPC log-range limits.
 *
 *  contract        ethers.Contract instance (read provider)
 *  filter          contract.filters.SomeEvent(...)
 *  fromBlock       optional start (default = ACTIVE_NETWORK.deployBlock or 0)
 *  toBlock         optional end (default = latest)
 *  onProgress      (scannedFrom, scannedTo, latest) => void  for UI updates
 *  signal          AbortSignal for cancellation
 *
 *  Returns: { events, error, scanned: { from, to } }
 */
export const scanEventsChunked = async (
  contract,
  filter,
  { fromBlock, toBlock, onProgress, signal } = {}
) => {
  if (!contract?.provider) {
    return { events: [], error: 'No provider', scanned: { from: 0, to: 0 } };
  }

  const provider = contract.provider;
  const chunkSize = ACTIVE_NETWORK.logChunkSize || 4900;
  const startFrom = typeof fromBlock === 'number'
    ? fromBlock
    : (ACTIVE_NETWORK.deployBlock || 0);

  let latest;
  try {
    latest = await provider.getBlockNumber();
  } catch (e) {
    return { events: [], error: 'Failed to fetch latest block', scanned: { from: 0, to: 0 } };
  }

  const endTo = typeof toBlock === 'number' ? toBlock : latest;
  if (endTo < startFrom) {
    return { events: [], error: null, scanned: { from: startFrom, to: endTo } };
  }

  const events = [];
  let cursor = startFrom;

  while (cursor <= endTo) {
    if (signal?.aborted) {
      return { events, error: 'Cancelled', scanned: { from: startFrom, to: cursor } };
    }
    const chunkTo = Math.min(cursor + chunkSize - 1, endTo);
    try {
      const chunk = await contract.queryFilter(filter, cursor, chunkTo);
      events.push(...chunk);
    } catch (e) {
      // RPC rejected this chunk — likely too large. Try halving once.
      const half = Math.floor(chunkSize / 2);
      const tryTo = Math.min(cursor + half - 1, endTo);
      try {
        const chunk = await contract.queryFilter(filter, cursor, tryTo);
        events.push(...chunk);
        cursor = tryTo + 1;
        onProgress?.(startFrom, cursor, endTo);
        continue;
      } catch (e2) {
        return {
          events,
          error: e2?.message || e?.message || 'RPC scan failed',
          scanned: { from: startFrom, to: cursor }
        };
      }
    }
    cursor = chunkTo + 1;
    onProgress?.(startFrom, cursor, endTo);
  }

  return { events, error: null, scanned: { from: startFrom, to: endTo } };
};
